/**
 * Validador de DNI, CIF, NIE, PASAPORTE
 *
 * Copiado de:
 * https://raw.githubusercontent.com/coixinet/spain-id/664c966dc654e419728a4d3c2e189b075caff8c9/src/index.js
 *
 * Añadido tipado y formateado.
 */
const DNI_REGEX = /^(\d{8})([A-Z])$/;
const CIF_REGEX = /^([ABCDEFGHJKLMNPQRSUVW])(\d{7})([0-9A-J])$/;
const NIE_REGEX = /^[XYZ]\d{7,8}[A-Z]$/;
const PASSPORT_REGEX = /^([a-zA-Z]{3}[0-9]{6})$/;

type DocumentType = "dni" | "cif" | "nie";

/**
 * Valida si el valor indicado es un documento español.
 *
 * @param str el valor a validar
 * @returns true si es dni, cif o nie. false en caso contrario
 */
export function isSpanishId(str: string): boolean {
  str = sanitize(str);
  let valid = false;
  const type = spainIdType(str);

  switch (type) {
    case "dni":
      valid = isValidDNI(str);
      break;
    case "nie":
      valid = isValidNIE(str);
      break;
    case "cif":
      valid = isValidCIF(str);
      break;
  }

  return valid;
}

/**
 * Determina si el documento es un DNI.
 *
 * @param str el valor a validar
 *
 * @returns true si es un DNI, false en caso contrario
 */
export function isValidDNI(str: string): boolean {
  str = sanitize(str);

  const dniLetters = "TRWAGMYFPDXBNJZSQVHLCKE";
  const letter = dniLetters.charAt(parseInt(str, 10) % 23);

  return letter === str.charAt(8);
}

/**
 * Determina si el documento es un NIE.
 *
 * @param str el valor a validar
 *
 * @returns true si es un NIE, false en caso contrario
 */
export function isValidNIE(str: string): boolean {
  str = sanitize(str);

  // Change the initial letter for the corresponding number and validate as DNI
  let niePrefix = str.charAt(0);

  switch (niePrefix) {
    case "X":
      niePrefix = "0";
      break;
    case "Y":
      niePrefix = "1";
      break;
    case "Z":
      niePrefix = "2";
      break;
    default:
      return false;
  }

  return isValidDNI(niePrefix + str.substr(1));
}

/**
 * Determina si el documento es un CIF.
 *
 * @param str el valor a validar
 *
 * @returns true si es un CIF, false en caso contrario
 */
export function isValidCIF(str: string): boolean {
  str = sanitize(str);
  if (!str || str.length !== 9) {
    return false;
  }

  const letters = ["J", "A", "B", "C", "D", "E", "F", "G", "H", "I"];
  const digits = str.substr(1, str.length - 2);
  const letter = str.substr(0, 1);
  const control = str.substr(str.length - 1);
  let sum = 0;
  let i;
  let digit: number;

  if (!letter.match(/[A-Z]/)) {
    return false;
  }

  for (i = 0; i < digits.length; ++i) {
    digit = parseInt(digits[i]);

    if (isNaN(digit)) {
      return false;
    }

    if (i % 2 === 0) {
      digit *= 2;
      if (digit > 9) {
        digit = Math.floor(digit / 10) + (digit % 10);
      }

      sum += digit;
    } else {
      sum += digit;
    }
  }

  sum %= 10;
  if (sum !== 0) {
    digit = 10 - sum;
  } else {
    digit = sum;
  }

  if (letter.match(/[ABEH]/)) {
    return String(digit) === control;
  }
  if (letter.match(/[NPQRSW]/)) {
    return letters[digit] === control;
  }

  return String(digit) === control || letters[digit] === control;
}

/**
 * Determina si el documento es un Pasaporte.
 *
 * @param str el valor a validar
 *
 * @returns true si es un Pasaporte, false en caso contrario
 */
export function isSpanishPassport(str: string): boolean {
  str = sanitize(str);

  /* Según nos indican no hay caracteres de control, pero realmente sí puede venir 1 letra de control.
  * En cualquier caso dejamos la validación que el cliente nos indica y por tanto no hay que hacer nada mas. */
  if (str && str.match(PASSPORT_REGEX)) {
    return true;
  }

  return false;
}

/**
 * Ensure uppercase and remove whitespace.
 */
function sanitize(str: string) {
  return str.toUpperCase();
}

/**
 * Determina el tipo de documento
 *
 * @param str el valor a validar
 *
 * @returns el tipo de documento o undefined si no se reconoce
 */
function spainIdType(str: string): DocumentType | undefined {
  str = sanitize(str);
  if (str.match(DNI_REGEX)) {
    return "dni";
  } else if (str.match(CIF_REGEX)) {
    return "cif";
  } else if (str.match(NIE_REGEX)) {
    return "nie";
  }

  return;
}
